import { useCallback } from 'react'
import {
  usePdfDesignLayerAsphaltPlacementsQuery,
  usePdfDesignLayerConcretePlacementsQuery,
  usePdfDesignLayerLotsQuery,
  usePdfDesignLayerDailyInspectionControlPointsQuery,
  usePdfDesignLayerDailyInspectionInspectionsQuery,
  usePdfDesignLayerDailyInspectionPunchListsQuery,
} from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { PDFDesignLayerType } from '../../../../../../types'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { LayerPreviewType } from '../../types'
import { DefaultOptionType } from 'components/CIMSSelect/types'
import { OptionalNullableKeysOfType } from '@cims-online/shared'
import { useProjectContext } from 'context/ProjectContext'

export function usePreviewOptions(
  typename: keyof typeof PDFDesignLayerType,
  globalId: string | undefined
): Record<
  keyof typeof LayerPreviewType,
  { options: DefaultOptionType[]; loading: boolean }
> {
  const { projectId } = useProjectContext()

  const queryOptions = useCallback(
    (notSkip: keyof typeof PDFDesignLayerType) => ({
      skip: typename !== notSkip,
      variables: { projectId },
      ...DefaultQueryHookOptions,
    }),
    [projectId, typename]
  )
  const asphaltPlacements = usePdfDesignLayerAsphaltPlacementsQuery(
    queryOptions('AsphaltMaterial')
  )
  const concretePlacements = usePdfDesignLayerConcretePlacementsQuery(
    queryOptions('ConcreteMaterial')
  )
  const lots = usePdfDesignLayerLotsQuery(queryOptions('MaterialRequirement'))
  const controlPoints = usePdfDesignLayerDailyInspectionControlPointsQuery(
    queryOptions('DailyInspectionWorkType')
  )
  const inspections = usePdfDesignLayerDailyInspectionInspectionsQuery(
    queryOptions('DailyInspectionWorkType')
  )
  const punchLists = usePdfDesignLayerDailyInspectionPunchListsQuery(
    queryOptions('DailyInspectionWorkType')
  )
  const filter = useCallback(
    <T,>(
      records: T[] | undefined,
      getGlobalId:
        | (OptionalNullableKeysOfType<T, string> & keyof T & string)
        | ((record: T) => string | null | undefined)
    ) => {
      return globalId
        ? records?.filter((record) => {
            const got =
              typeof getGlobalId === 'string'
                ? (record[getGlobalId] as string | null | undefined)
                : getGlobalId(record)
            return got?.toLowerCase() === globalId.toLowerCase()
          })
        : records
    },
    [globalId]
  )
  return {
    AsphaltPlacement: {
      options: useOptions({
        items: filter(
          asphaltPlacements.data?.asphaltPlacements.records,
          ({ asphaltLot }) => asphaltLot?.asphaltMaterialsGuid
        ),
        sort: 'placementId',
        label: 'placementId',
        value: 'globalId',
      }),
      loading: asphaltPlacements.loading,
    },
    ConcretePlacement: {
      options: useOptions({
        items: filter(
          concretePlacements.data?.concretePlacements.records,
          ({ concreteTestSet }) => concreteTestSet?.concreteMaterialsGuid
        ),
        sort: 'placementId',
        label: 'placementId',
        value: 'globalId',
      }),
      loading: concretePlacements.loading,
    },
    DailyInspectionControlPoint: {
      options: useOptions({
        items: filter(
          controlPoints.data?.dailyInspectionControlPoints.records,
          'workTypeGuid'
        ),
        sort: 'controlPointId',
        label: 'controlPointId',
        value: 'globalId',
      }),
      loading: controlPoints.loading,
    },
    Lot: {
      options: useOptions({
        items: filter(lots.data?.lots.records, 'materialGuid'),
        sort: 'lotId',
        label: 'lotId',
        value: 'globalId',
      }),
      loading: lots.loading,
    },
    DailyInspectionInspection: {
      options: useOptions({
        items: filter(
          inspections.data?.dailyInspectionInspections.records,
          'workTypeGuid'
        ),
        sort: 'inspectionId',
        label: 'inspectionId',
        value: 'globalId',
      }),
      loading: inspections.loading,
    },
    DailyInspectionPunchList: {
      options: useOptions({
        items: filter(
          punchLists.data?.dailyInspectionPunchLists.records,
          'workTypeGuid'
        ),
        sort: 'punchListId',
        label: 'punchListId',
        value: 'globalId',
      }),
      loading: punchLists.loading,
    },
  }
}
